import { createContext, useContext } from 'react'
import type {
  AccountRepository,
  BreakdownRepository,
  RecadoRepository,
  CommentRepository,
  NotificationRepository,
  ProductRepository,
  CategoryRepository,
  CreditCardRepository,
  InvoiceRepository,
  ProviderAccountRepository,
  TransactionRepository,
} from '@domain/ports/finance-repository'
import type { ListRepository } from '@domain/ports/list-repository'
import type { ReceiptRepository } from '@domain/ports/receipt-repository'
import type { AgentInstructionsRepository } from '@domain/ports/agent-instructions-repository'
import type { CategoryRuleRepository } from '@domain/ports/category-rule-repository'
import type { ProductLookup } from '@domain/ports/product-lookup'

/**
 * O acesso da UI ao núcleo financeiro, sempre pelas portas — nunca pelo
 * adapter. Mesma forma de `household-context.ts`, e em módulo próprio pelo
 * mesmo motivo: um arquivo que exporta componente **e** outra coisa quebra o
 * Fast Refresh, e o `react-refresh/only-export-components` reprova.
 *
 * **Um contexto para as portas, e não um por porta.** Elas chegam juntas do
 * mesmo lugar (`src/main.tsx`) e vivem o app inteiro; um provider por porta
 * seria uma chance a mais de esquecer alguma a cada porta nova. O que continua
 * separado é a **prop** de cada uma em `AppProviders`: é ela que faz omitir uma
 * virar erro de `tsc`, e é essa garantia que a decisão 69 comprou — e ela
 * cobrou de novo quando a quinta entrou, na Fase 3.
 */
export type FinanceRepositories = {
  accountRepository: AccountRepository
  creditCardRepository: CreditCardRepository
  categoryRepository: CategoryRepository
  transactionRepository: TransactionRepository
  /** O catálogo do provider — só leitura. Ver a porta. */
  providerAccountRepository: ProviderAccountRepository
  /** As faturas do mês, para o card da tela de Transações. Só leitura. */
  invoiceRepository: InvoiceRepository
  /** Os itens de detalhamento de uma transação. */
  breakdownRepository: BreakdownRepository
  /** A thread de comentários de uma entidade. */
  commentRepository: CommentRepository
  recadoRepository: RecadoRepository
  /** A central de notificações do membro logado. */
  notificationRepository: NotificationRepository
  /** O catálogo de produtos e o histórico de preço. */
  productRepository: ProductRepository
  /** As listas — a única porta que lê **por assinatura**. Ver a porta. */
  listRepository: ListRepository
  /** As notas fiscais. Uma nota também é lida ao vivo: ela muda sozinha. */
  receiptRepository: ReceiptRepository
  /** O contexto que os agentes leem antes de caçar preço. */
  agentInstructionsRepository: AgentInstructionsRepository
  /** A tabela de regras que categoriza sozinha — Fase 10. */
  categoryRuleRepository: CategoryRuleRepository
  /** A busca de produto por código de barras, numa base pública. */
  productLookup: ProductLookup
}

export const FinanceRepositoriesContext = createContext<FinanceRepositories | null>(null)

export function useFinanceRepositories(): FinanceRepositories {
  const value = useContext(FinanceRepositoriesContext)
  if (value === null) {
    throw new Error(
      'useFinanceRepositories: falta um <FinanceRepositoriesContext.Provider> acima na árvore.',
    )
  }
  return value
}
